import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { transparentize } from 'polished';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useTheme } from 'src/components/muiTheme/utils';

import type { AppAlert as AppAlertType } from 'src/models/conversation/_types';

export type AppAlertBannerVariant = 'short' | 'tall';

interface AppAlertProps {
  alert: AppAlertType;
  onButtonClick?: (url: string) => void;
  bannerVariant?: AppAlertBannerVariant;
  onClose?: () => void;
}

export const AppAlert = ({
  alert,
  onButtonClick,
  bannerVariant,
  onClose,
}: AppAlertProps) => {
  const theme = useTheme();

  const highContrastButtonStyles = {
    backgroundColor: transparentize(0.3, theme.palette.common.white),
    border: `1px solid ${transparentize(0.9, theme.palette.common.black)}`,
    '&:hover': {
      backgroundColor: transparentize(0.3, theme.palette.common.white),
    },
    ...theme.applyStyles('dark', {
      backgroundColor: transparentize(0.3, theme.palette.common.black),
      border: `1px solid ${transparentize(0.9, theme.palette.common.white)}`,
      '&:hover': {
        backgroundColor: transparentize(0.3, theme.palette.common.black),
      },
    }),
  };

  const getBackgroundColor = () => {
    const bgColor = alert.bgColor || 'background2';
    const resolvedColor = bgColor.startsWith('#')
      ? bgColor
      : theme.palette[bgColor]?.main || theme.palette.background.paper;

    if (alert.bgTransparent) {
      return transparentize(0.6, resolvedColor);
    }
    return resolvedColor;
  };

  const getTextColor = () => {
    if (!alert.textColor || alert.textColor === 'default') {
      return theme.palette.text.primary;
    }

    if (alert.textColor.includes('darkmode')) {
      return alert.textColor.includes('2')
        ? transparentize(0.2, theme.palette.common.white)
        : theme.palette.common.white;
    }

    return alert.textColor.includes('2')
      ? theme.palette.text.secondary
      : theme.palette.text.primary;
  };

  const getButtonVariant = () => {
    switch (alert.buttonStyle) {
      case 'outline':
        return 'bordered';
      case 'plain':
        return 'borderless';
      default:
        return 'prominent';
    }
  };

  const getButtonColor = () => {
    switch (alert.buttonColor) {
      case 'default':
        return 'neutral';
      case 'error':
        return 'error';
      case 'secondary':
      case 'success':
      case 'primary':
        return alert.buttonColor;
      default:
        return 'primary';
    }
  };

  const handleAction = () => {
    if (alert.buttonUrl) {
      onButtonClick?.(alert.buttonUrl);
    }
  };

  const effectiveBannerVariant =
    bannerVariant || (alert.bgImageUrl ? 'tall' : 'short');
  const bannerImageUrl =
    effectiveBannerVariant === 'tall'
      ? alert.bgImageUrl || alert.heroImageUrl
      : alert.heroImageUrl;

  const showBanner = alert.alertType === 'banner';
  const showCloseButton = !!onClose && !alert.hideCloseButton;
  const backgroundImageStyles =
    alert.bgType === 'image' && alert.bgImageUrl
      ? {
          backgroundImage: `url(${alert.bgImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : null;

  if (showBanner) {
    const tallVariant =
      effectiveBannerVariant === 'tall' && !!alert.bgImageUrl;

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          p: 2,
          boxSizing: 'border-box',
        }}
      >
        <Box
          onClick={handleAction}
          sx={{
            width: '100%',
            aspectRatio: tallVariant ? '16 / 9' : '3.2 / 1',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'divider',
            backgroundImage: bannerImageUrl ? `url(${bannerImageUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: alert.buttonUrl ? 'pointer' : 'default',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: getBackgroundColor(),
        ...(backgroundImageStyles || {}),
      }}
    >
      <Stack
        spacing={3}
        sx={{
          height: '100%',
          justifyContent: alert.verticalAlignment || 'flex-start',
          alignItems: 'center',
          p: 3,
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {showCloseButton && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={[
              {
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
              alert.highContrastButton ? highContrastButtonStyles : null,
            ]}
          >
            <Icon icon={faXmark} size="xsmall" color="inherit" />
          </IconButton>
        )}

        {alert.headingText && (
          <Typography
            variant="h6"
            align="center"
            color={getTextColor()}
            sx={{ whiteSpace: 'pre-line', fontWeight: 500 }}
          >
            {alert.headingText}
          </Typography>
        )}

        {alert.heroImageUrl && (
          <Box
            component="img"
            src={alert.heroImageUrl}
            sx={{
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        )}

        {alert.buttonCopy && (
          <Button
            variant={getButtonVariant()}
            color={getButtonColor()}
            onClick={handleAction}
            sx={{ minWidth: 200 }}
          >
            {alert.buttonCopy}
          </Button>
        )}

        {alert.textBlock && (
          <Typography
            variant="body2"
            align="center"
            color={getTextColor()}
            sx={{ whiteSpace: 'pre-line' }}
          >
            {alert.textBlock}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
